import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Search } from "lucide-react";
import { apiError, recruitersAdminApi } from "../api";
import { chileToday, dayLong, money, PROGRAM_MIN_DATE } from "../format";
import { Field, Modal } from "../ui";
import type { PortfolioProducer, Recruiter, RecruiterEventRow, RecruiterStatus } from "../types";
import { useToast } from "@/app/providers/Toaster";
import { useDebounce } from "@/lib/utils/useDebounce";

function useDone(message: string, onClose: () => void) {
  const { push } = useToast();
  const qc = useQueryClient();
  return {
    onSuccess: () => {
      push({ kind: "success" as const, message });
      qc.invalidateQueries({ queryKey: ["recruiters"] });
      onClose();
    },
    onError: (err: unknown) => push({ kind: "error" as const, message: apiError(err) }),
  };
}

function Footer({ onClose, disabled, pending, label, danger }: { onClose: () => void; disabled?: boolean; pending?: boolean; label: string; danger?: boolean }) {
  return (
    <>
      <button type="button" className="nx-btn-ghost" onClick={onClose}>
        Cancelar
      </button>
      <button type="submit" form="nx-modal-form" className={danger ? "nx-btn-danger" : "nx-btn-primary"} disabled={disabled || pending}>
        {pending && <Loader2 size={14} className="animate-spin" />}
        {label}
      </button>
    </>
  );
}

export function EditRecruiterModal({ recruiter, onClose }: { recruiter: Recruiter; onClose: () => void }) {
  const [name, setName] = useState(recruiter.name);
  const [phone, setPhone] = useState(recruiter.phone ?? "");
  const [rut, setRut] = useState(recruiter.rut ?? "");
  const [signedOn, setSignedOn] = useState(recruiter.agreementSignedOn ?? "");
  const [version, setVersion] = useState(recruiter.agreementVersion ?? "");
  const [notes, setNotes] = useState(recruiter.internalNotes ?? "");
  const [status, setStatus] = useState<RecruiterStatus>(recruiter.status);
  const [endedOn, setEndedOn] = useState(recruiter.endedOn ?? "");
  const [endReason, setEndReason] = useState(recruiter.endReason ?? "");
  const mut = useMutation({
    mutationFn: () =>
      recruitersAdminApi.update(recruiter.id, {
        name: name.trim(),
        phone: phone.trim(),
        rut: rut.trim(),
        agreementSignedOn: signedOn,
        agreementVersion: version.trim(),
        internalNotes: notes,
        status,
        ...(status === "ended" ? { endedOn: endedOn || undefined, endReason: endReason.trim() } : {}),
      }),
    ...useDone("Cambios guardados.", onClose),
  });
  return (
    <Modal title={`Editar a ${recruiter.name}`} onClose={onClose} footer={<Footer onClose={onClose} pending={mut.isPending} disabled={name.trim().length < 2} label="Guardar" />}>
      <form id="nx-modal-form" className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <Field label="Nombre" required>
          <input className="nx-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Teléfono">
            <input className="nx-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <Field label="RUT">
            <input className="nx-input" value={rut} onChange={(e) => setRut(e.target.value)} />
          </Field>
          <Field label="Firma del acuerdo">
            <input className="nx-input" type="date" min={PROGRAM_MIN_DATE} max={chileToday()} autoComplete="off" value={signedOn} onChange={(e) => setSignedOn(e.target.value)} />
          </Field>
          <Field label="Versión del acuerdo">
            <input className="nx-input" value={version} onChange={(e) => setVersion(e.target.value)} />
          </Field>
        </div>
        <Field label="Estado" hint="En pausa: sigue viendo su espacio. Terminado (B11): ya no genera comisión por eventos posteriores; se le paga lo devengado.">
          <select className="nx-input" value={status} onChange={(e) => setStatus(e.target.value as RecruiterStatus)}>
            <option value="active">Activo</option>
            <option value="paused">En pausa</option>
            <option value="ended">Terminado</option>
          </select>
        </Field>
        {status === "ended" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Fecha de término" hint="Si la dejas vacía, hoy.">
              <input className="nx-input" type="date" value={endedOn} onChange={(e) => setEndedOn(e.target.value)} />
            </Field>
            <Field label="Motivo">
              <input className="nx-input" value={endReason} onChange={(e) => setEndReason(e.target.value)} />
            </Field>
          </div>
        )}
        <Field label="Notas internas" hint="Sólo las ve Mixcore.">
          <textarea className="nx-input min-h-[72px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

export function AssignProducerModal({ recruiterId, recruiterName, onClose }: { recruiterId: number; recruiterName: string; onClose: () => void }) {
  const [search, setSearch] = useState("");
  const q = useDebounce(search, 300);
  const [picked, setPicked] = useState<number | null>(null);
  const [registeredOn, setRegisteredOn] = useState("");
  const list = useQuery({ queryKey: ["recruiters", "prospects", q], queryFn: () => recruitersAdminApi.searchProspects(q) });
  const pickedRow = list.data?.prospects.find((p) => p.id === picked) ?? null;
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.assignProducer(recruiterId, { prospectId: picked!, registeredOn: registeredOn || undefined }),
    ...useDone(`Productor asignado a ${recruiterName}.`, onClose),
  });
  return (
    <Modal
      title={`Asignar productor a ${recruiterName}`}
      onClose={onClose}
      wide
      footer={<Footer onClose={onClose} pending={mut.isPending} disabled={!picked || !!pickedRow?.assignedTo} label="Asignar" />}
    >
      <form id="nx-modal-form" className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (picked) mut.mutate(); }}>
        <div className="rounded-md border border-border bg-bg-subtle p-3 text-sm text-fg-muted space-y-2">
          <p>
            <b className="text-fg">¿Ya está en el CRM?</b> Búscalo abajo y elígelo.{" "}
            <b className="text-fg">¿Es un productor nuevo?</b> Créalo primero en el CRM (tipo Productor) y vuelve acá
            a asignarlo. Si ya tiene cuenta en Mixcore, enlázala en la ficha del prospecto para que se cuenten sus eventos.
          </p>
          <a href="/seguimiento/funnel?new=productor" target="_blank" rel="noreferrer" className="nx-btn-secondary inline-flex">
            <ExternalLink size={14} /> Crear productor nuevo en el CRM
          </a>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
          <input className="nx-input pl-8" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar productor por nombre o correo…" autoFocus />
        </div>
        <ul className="max-h-72 overflow-y-auto divide-y divide-border rounded-md border border-border">
          {list.isLoading && <li className="p-3 text-sm text-fg-muted">Buscando…</li>}
          {list.data?.prospects.length === 0 && (
            <li className="p-3 text-sm text-fg-muted">Sin resultados. Si es un productor nuevo, créalo en el CRM con el botón de arriba y vuelve a buscarlo.</li>
          )}
          {list.data?.prospects.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                disabled={!!p.assignedTo}
                onClick={() => {
                  setPicked(p.id);
                  setRegisteredOn(chileToday());
                }}
                className={`w-full text-left px-3 py-2.5 text-sm ${picked === p.id ? "bg-accent-soft" : "hover:bg-bg-hover"} disabled:opacity-60`}
              >
                <div className="font-medium text-fg">{p.name}</div>
                <div className="text-xs text-fg-muted">
                  {p.contactEmail ?? "sin correo"} · en el CRM desde el {dayLong(p.createdOn)}
                  {p.assignedTo ? ` · ya es de ${p.assignedTo.name}` : ""}
                </div>
              </button>
            </li>
          ))}
        </ul>
        {pickedRow && (
          <Field
            label="Atribución desde"
            hint="Por defecto, hoy: sólo cuentan sus eventos desde esta fecha y los anteriores no generan comisión. Pon una fecha anterior sólo si el reclutador lo registró antes (B8): se le sumarán los eventos desde entonces."
          >
            <input className="nx-input" type="date" min={PROGRAM_MIN_DATE} max={chileToday()} value={registeredOn} onChange={(e) => setRegisteredOn(e.target.value)} />
          </Field>
        )}
      </form>
    </Modal>
  );
}

export function RedateProducerModal({ producer, onClose }: { producer: PortfolioProducer; onClose: () => void }) {
  const [date, setDate] = useState(producer.registeredOn ?? chileToday());
  const [note, setNote] = useState("");
  const changed = !!date && date !== producer.registeredOn;
  const later = !!producer.registeredOn && date > producer.registeredOn;
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.decideProducer(producer.id, { action: "redate", registeredOn: date, note: note.trim() || undefined }),
    ...useDone(`Atribución de ${producer.name} desde el ${dayLong(date)}.`, onClose),
  });
  return (
    <Modal title={`Fecha de atribución · ${producer.name}`} onClose={onClose} footer={<Footer onClose={onClose} pending={mut.isPending} disabled={!changed} label="Cambiar fecha" />}>
      <form id="nx-modal-form" className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (changed) mut.mutate(); }}>
        <p className="text-sm text-fg-muted">
          Sólo cuentan sus eventos desde esta fecha. Ahora parte el {dayLong(producer.registeredOn)}.
        </p>
        <Field label="Atribución desde" required>
          <input className="nx-input" type="date" min={PROGRAM_MIN_DATE} max={chileToday()} value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        {changed && later && (
          <p className="text-sm text-warning">
            Los eventos anteriores al {dayLong(date)} salen de su cartera y se borran sus comisiones (salvo las ya liquidadas).
          </p>
        )}
        {changed && !later && <p className="text-sm text-fg-muted">Se le suman los eventos desde el {dayLong(date)} que no tenga otro reclutador.</p>}
        <Field label="Motivo (opcional)" hint="Queda en la auditoría.">
          <textarea className="nx-input min-h-[60px]" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

export function ReleaseProducerModal({ producer, onClose }: { producer: PortfolioProducer; onClose: () => void }) {
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.decideProducer(producer.id, { action: "release", note: note.trim() }),
    ...useDone(`${producer.name} quedó liberado.`, onClose),
  });
  return (
    <Modal title={`Liberar a ${producer.name}`} onClose={onClose} footer={<Footer onClose={onClose} pending={mut.isPending} disabled={!note.trim()} label="Liberar" danger />}>
      <form id="nx-modal-form" className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (note.trim()) mut.mutate(); }}>
        <p className="text-sm text-fg-muted">
          Sale de su cartera. Lo ya liquidado se mantiene; se anulan sus comisiones aún no liquidadas y sus eventos futuros
          quedan libres para otro reclutador.
        </p>
        <Field label="Motivo" required hint="El reclutador lo ve.">
          <textarea className="nx-input min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        </Field>
      </form>
    </Modal>
  );
}

const REVIEW_COPY = {
  approve: { title: "Aprobar checklist", label: "Aprobar", needsNote: false, help: "La comisión del evento queda por pagar cuando el evento esté conciliado." },
  observe: { title: "Observar un ítem", label: "Enviar observación", needsNote: true, help: "Le avisamos por correo. Tiene 5 días hábiles para subsanarlo; sólo si no, la comisión se retiene." },
  retain: { title: "Retener comisión", label: "Retener", needsNote: false, help: "Lo observado no se subsanó dentro del plazo (Anexo 1)." },
  reopen: { title: "Reabrir revisión", label: "Reabrir", needsNote: false, help: "Vuelve a 'por revisar'." },
} as const;

export function ReviewModal({ ev, action, onClose }: { ev: RecruiterEventRow; action: keyof typeof REVIEW_COPY; onClose: () => void }) {
  const copy = REVIEW_COPY[action];
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.reviewEvent(ev.id, { action, note: note.trim() || undefined }),
    ...useDone(action === "observe" ? "Observación enviada al reclutador." : "Listo.", onClose),
  });
  return (
    <Modal
      title={`${copy.title} · ${ev.name}`}
      onClose={onClose}
      footer={<Footer onClose={onClose} pending={mut.isPending} disabled={copy.needsNote && !note.trim()} label={copy.label} danger={action === "retain"} />}
    >
      <form id="nx-modal-form" className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!copy.needsNote || note.trim()) mut.mutate(); }}>
        <p className="text-sm text-fg-muted">{copy.help}</p>
        <Field label={copy.needsNote ? "¿Qué falta?" : "Nota (opcional)"} required={copy.needsNote}>
          <textarea className="nx-input min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        </Field>
      </form>
    </Modal>
  );
}

export function AdjustmentModal({ recruiterId, onClose }: { recruiterId: number; onClose: () => void }) {
  const [sign, setSign] = useState<1 | -1>(1);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const n = Math.round(Number(amount.replace(/\D/g, "")) || 0);
  const mut = useMutation({
    mutationFn: () => recruitersAdminApi.createAdjustment(recruiterId, { amount: sign * n, description: description.trim() }),
    ...useDone("Ajuste agregado: entra en la próxima liquidación.", onClose),
  });
  return (
    <Modal title="Ajuste manual" onClose={onClose} footer={<Footer onClose={onClose} pending={mut.isPending} disabled={!n || description.trim().length < 3} label="Agregar ajuste" />}>
      <form id="nx-modal-form" className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <p className="text-sm text-fg-muted">
          Para casos que el cálculo automático no cubre (un evento con condiciones especiales, una corrección). Entra en la
          próxima liquidación y el reclutador ve la descripción.
        </p>
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 items-end">
          <Field label="Tipo">
            <select className="nx-input" value={sign} onChange={(e) => setSign(Number(e.target.value) as 1 | -1)}>
              <option value={1}>A favor</option>
              <option value={-1}>Descuento</option>
            </select>
          </Field>
          <Field label="Monto bruto" required hint={n ? `${sign < 0 ? "−" : "+"}${money(n)}` : undefined}>
            <input className="nx-input tabular-nums" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="10000" />
          </Field>
        </div>
        <Field label="Descripción" required>
          <input className="nx-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ej.: evento 34 con comisión fija negociada" />
        </Field>
      </form>
    </Modal>
  );
}
